/**
 * Γεωμετρία και αρίθμηση της κάτοψης — καθαρές συναρτήσεις, χωρίς React.
 *
 * Μοντέλο συντεταγμένων (ίδιο με την οθόνη του ταμείου, DineInOperations):
 *   • position_x / position_y: το ΚΕΝΤΡΟ του τραπεζιού, σε ποσοστό του καμβά.
 *   • width / height: «μονάδες μεγέθους», × SIZE_UNIT_PX = pixels.
 * Επειδή η θέση είναι ποσοστό και το μέγεθος pixels, ό,τι χρειάζεται «άκρες»
 * (στοίχιση, όρια, πλέγμα) θέλει και το μέγεθος του καμβά.
 */

/** Μία μονάδα μεγέθους σε pixels. Το διαβάζει και το ταμείο — ένα σημείο αλήθειας. */
export const SIZE_UNIT_PX = 6;

/**
 * Ο ΛΟΓΙΚΟΣ καμβάς της σάλας. Editor και ταμείο ζωγραφίζουν πάντα σε αυτές τις
 * διαστάσεις και μετά τον κλιμακώνουν ολόκληρο (με σταθερή αναλογία) ώστε να
 * χωρέσει στην οθόνη. Έτσι η κάτοψη δείχνει ίδια σε κάθε οθόνη.
 *
 * Πριν, ο καμβάς έπαιρνε όποιο μέγεθος είχε η οθόνη ενώ τα τραπέζια είχαν σταθερά
 * pixels: σε μικρή οθόνη ταμείου τα τραπέζια έπεφταν το ένα πάνω στο άλλο.
 * 5:3 ≈ ο χώρος της σάλας σε ταμείο 1024×768, η πιο στενή οθόνη που συναντάμε.
 */
export const FLOOR_CANVAS = { width: 1000, height: 600 } as const;

/** Συντελεστής κλίμακας ώστε ο λογικός καμβάς να χωρά ολόκληρος στο διαθέσιμο πλαίσιο. */
export function fitScale(available: { width: number; height: number }, canvas: { width: number; height: number } = FLOOR_CANVAS): number {
  if (!(available.width > 0) || !(available.height > 0)) return 0;
  return Math.min(available.width / canvas.width, available.height / canvas.height);
}

/**
 * Καμβάς ζώνης: πλάτος πάντα 1000, ύψος από το σχήμα της περιοχής στη Sala (layout w×h).
 * Χωρίς διάταξη: ο κλασικός 1000×600.
 */
export function zoneCanvas(layout?: { w?: number; h?: number } | null): { width: number; height: number } {
  const w = Number(layout?.w);
  const h = Number(layout?.h);
  if (!(w > 0) || !(h > 0)) return FLOOR_CANVAS;
  return { width: FLOOR_CANVAS.width, height: Math.round(FLOOR_CANVAS.width * Math.min(3, Math.max(0.2, h / w))) };
}
/** Βήμα «κουμπώματος» κατά το σύρσιμο, σε ποσοστό του καμβά. */
export const SNAP_STEP = 2;
export const MIN_SIZE_UNITS = 5;
export const MAX_SIZE_UNITS = 25;
export const MAX_BULK_TABLES = 100;
export const MAX_CAPACITY = 99;
/** Κενό ανάμεσα σε τραπέζια όταν τα τοποθετούμε εμείς σε πλέγμα. */
export const GRID_GAP_PX = 24;

export type TableShape = 'round' | 'square' | 'rectangle';
export type TableStatus = 'available' | 'occupied' | 'receipt_printed' | 'cleaning' | 'reserved' | string;

export interface FloorTable {
  id: number;
  zone_id: number;
  table_number: string;
  name: string | null;
  capacity: number;
  shape: TableShape;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  is_reservable: boolean;
  status: TableStatus;
  is_active: boolean;
}

export interface CanvasSize { width: number; height: number; }
export interface Point { x: number; y: number; }
type Sized = Pick<FloorTable, 'width' | 'height'>;
type Placed = Pick<FloorTable, 'id' | 'position_x' | 'position_y' | 'width' | 'height'>;

const finite = (value: unknown, fallback: number): number => {
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : fallback;
};

const clamp = (value: number, min: number, max: number) =>
  min > max ? (min + max) / 2 : Math.min(max, Math.max(min, value));

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Το PostgreSQL στέλνει τα NUMERIC ως κείμενο· εδώ γίνονται αριθμοί μία φορά. */
export function normalizeTable(raw: any): FloorTable {
  return {
    id: Number(raw.id),
    zone_id: Number(raw.zone_id),
    table_number: String(raw.table_number ?? ''),
    name: raw.name || null,
    capacity: Math.round(finite(raw.capacity, 4)) || 4,
    shape: (['round', 'square', 'rectangle'] as const).includes(raw.shape) ? raw.shape : 'square',
    position_x: finite(raw.position_x, 10),
    position_y: finite(raw.position_y, 10),
    width: finite(raw.width, 10) || 10,
    height: finite(raw.height, 10) || 10,
    is_reservable: raw.is_reservable !== false,
    status: raw.status || 'available',
    is_active: raw.is_active !== false
  };
}

/** «2» πριν από το «10», «A2» πριν από το «A10». */
export function compareTableNumbers(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

// ── Αρίθμηση ────────────────────────────────────────────────────────────────

/** Ο επόμενος ελεύθερος αύξων αριθμός για το πρόθεμα (κενό πρόθεμα = σκέτοι αριθμοί). */
export function nextTableNumber(taken: ReadonlySet<string> | readonly string[], prefix = ''): number {
  let max = 0;
  taken.forEach((number: string) => {
    if (!number.startsWith(prefix)) return;
    const rest = number.slice(prefix.length);
    if (/^\d+$/.test(rest)) max = Math.max(max, parseInt(rest, 10));
  });
  return max + 1;
}

export interface NumberingOptions {
  prefix?: string;
  start: number;
  count: number;
  taken: ReadonlySet<string>;
  /** true: πηδά όσους υπάρχουν ήδη· false: τους επιστρέφει ως συγκρούσεις. */
  skipTaken: boolean;
}

/**
 * Οι αριθμοί που θα πάρουν `count` νέα τραπέζια.
 * Με skipTaken παράγονται πάντα `count` αριθμοί (οι πιασμένοι παραλείπονται)·
 * χωρίς, η σειρά είναι συνεχόμενη και οι πιασμένοι γυρίζουν στο `conflicts`.
 */
export function generateTableNumbers(options: NumberingOptions): { numbers: string[]; conflicts: string[] } {
  const prefix = (options.prefix ?? '').trim();
  const count = clamp(Math.floor(options.count) || 0, 0, MAX_BULK_TABLES);
  const numbers: string[] = [];
  const conflicts: string[] = [];
  let current = Math.max(0, Math.floor(options.start) || 0);

  while (numbers.length < count) {
    const candidate = `${prefix}${current++}`;
    if (options.taken.has(candidate)) {
      if (options.skipTaken) continue;
      conflicts.push(candidate);
    }
    numbers.push(candidate);
  }
  return { numbers, conflicts };
}

/** «1–12», «A1–A8», ή η πλήρης λίστα όταν η σειρά έχει κενά. */
export function describeNumberRange(numbers: string[]): string {
  if (numbers.length === 0) return '';
  if (numbers.length <= 2) return numbers.join(', ');
  const tail = (s: string) => { const m = s.match(/(\d+)$/); return m ? parseInt(m[1], 10) : NaN; };
  const contiguous = numbers.every((n, i) => i === 0 || tail(n) === tail(numbers[i - 1]) + 1);
  return contiguous ? `${numbers[0]}–${numbers[numbers.length - 1]}` : numbers.join(', ');
}

// ── Γεωμετρία ───────────────────────────────────────────────────────────────

/** Μισό πλάτος/ύψος του τραπεζιού, σε ποσοστό του καμβά. */
export function halfExtent(table: Sized, canvas: CanvasSize): Point {
  return {
    x: canvas.width > 0 ? ((table.width * SIZE_UNIT_PX) / 2 / canvas.width) * 100 : 0,
    y: canvas.height > 0 ? ((table.height * SIZE_UNIT_PX) / 2 / canvas.height) * 100 : 0
  };
}

/** Κρατά ολόκληρο το τραπέζι μέσα στον καμβά. */
export function clampToCanvas(point: Point, table: Sized, canvas: CanvasSize): Point {
  const half = halfExtent(table, canvas);
  return {
    x: round2(clamp(point.x, half.x, 100 - half.x)),
    y: round2(clamp(point.y, half.y, 100 - half.y))
  };
}

const snap = (value: number) => Math.round(value / SNAP_STEP) * SNAP_STEP;
// round2 πρώτα: το 84/600*100 βγαίνει 14,000000000000002 και θα ανέβαινε στο 16.
const snapUp = (value: number) => Math.ceil(round2(value) / SNAP_STEP) * SNAP_STEP;

/**
 * Μετατόπιση μιας ομάδας τραπεζιών που σύρεται μαζί.
 * Κουμπώνει στο πλέγμα το τραπέζι που κρατά ο χρήστης και εφαρμόζει την ΙΔΙΑ
 * μετατόπιση σε όλα — οι μεταξύ τους αποστάσεις δεν αλλάζουν ποτέ. Η ομάδα
 * σταματά στα όρια του καμβά σαν ένα σώμα.
 */
export function groupDragDelta(
  group: Placed[],
  primaryId: number,
  rawDx: number,
  rawDy: number,
  canvas: CanvasSize,
  useSnap = true
): Point {
  const primary = group.find(t => t.id === primaryId) ?? group[0];
  if (!primary) return { x: 0, y: 0 };

  let dx = useSnap ? snap(primary.position_x + rawDx) - primary.position_x : rawDx;
  let dy = useSnap ? snap(primary.position_y + rawDy) - primary.position_y : rawDy;

  let minDx = -Infinity, maxDx = Infinity, minDy = -Infinity, maxDy = Infinity;
  for (const table of group) {
    const half = halfExtent(table, canvas);
    minDx = Math.max(minDx, half.x - table.position_x);
    maxDx = Math.min(maxDx, 100 - half.x - table.position_x);
    minDy = Math.max(minDy, half.y - table.position_y);
    maxDy = Math.min(maxDy, 100 - half.y - table.position_y);
  }
  // Ομάδα φαρδύτερη από τον καμβά (ή ήδη εκτός ορίων): μην την τραβήξεις βίαια.
  dx = minDx > maxDx ? 0 : clamp(dx, minDx, maxDx);
  dy = minDy > maxDy ? 0 : clamp(dy, minDy, maxDy);
  return { x: round2(dx), y: round2(dy) };
}

export interface GridOptions {
  count: number;
  columns: number;
  /** Μέγεθος κελιού σε μονάδες (το μεγαλύτερο τραπέζι της ομάδας). */
  cell: Sized;
  canvas: CanvasSize;
  /** Κέντρο του πρώτου τραπεζιού, σε ποσοστό. Χωρίς αυτό: πάνω αριστερά. */
  origin?: Point;
  gapPx?: number;
}

/**
 * Θέσεις (κέντρα) για `count` τραπέζια σε πλέγμα `columns` στηλών.
 * `fits` = false όταν το πλέγμα βγαίνει από τον καμβά· οι θέσεις τότε
 * μαζεύονται στο όριο, ώστε κανένα τραπέζι να μη χαθεί εκτός οθόνης.
 */
export function gridPositions(options: GridOptions): { positions: Point[]; fits: boolean; step: Point } {
  const { canvas, cell } = options;
  const columns = Math.max(1, Math.floor(options.columns) || 1);
  const gap = options.gapPx ?? GRID_GAP_PX;
  const half = halfExtent(cell, canvas);

  // Το βήμα στρογγυλεύεται ΠΑΝΩ στο πλέγμα κουμπώματος: ίσες αποστάσεις που
  // μένουν ίσες και μετά το πρώτο σύρσιμο.
  const stepOf = (units: number, extent: number) =>
    extent > 0 ? snapUp((((units * SIZE_UNIT_PX) + gap) / extent) * 100) : SNAP_STEP;
  const step = { x: stepOf(cell.width, canvas.width), y: stepOf(cell.height, canvas.height) };

  const margin = { x: canvas.width > 0 ? (gap / canvas.width) * 100 : 0, y: canvas.height > 0 ? (gap / canvas.height) * 100 : 0 };
  const origin = options.origin ?? {
    x: snapUp(half.x + margin.x),
    y: snapUp(half.y + margin.y)
  };

  let fits = true;
  const positions: Point[] = [];
  for (let i = 0; i < options.count; i++) {
    const raw = { x: origin.x + (i % columns) * step.x, y: origin.y + Math.floor(i / columns) * step.y };
    const placed = clampToCanvas(raw, cell, canvas);
    if (Math.abs(placed.x - raw.x) > 0.01 || Math.abs(placed.y - raw.y) > 0.01) fits = false;
    positions.push(placed);
  }
  return { positions, fits, step };
}

/** Πόσες στήλες του κελιού χωράνε στο πλάτος του καμβά. */
export function maxColumns(cell: Sized, canvas: CanvasSize, gapPx = GRID_GAP_PX): number {
  if (canvas.width <= 0) return 1;
  const { step } = gridPositions({ count: 1, columns: 1, cell, canvas, gapPx });
  const half = halfExtent(cell, canvas);
  const start = snapUp(half.x + (gapPx / canvas.width) * 100);
  return Math.max(1, Math.floor((100 - half.x - start) / step.x) + 1);
}

/**
 * Πού να μπει ένα νέο μπλοκ τραπεζιών: κάτω από τα υπάρχοντα αν χωράει,
 * αλλιώς πάνω αριστερά (ο χρήστης το σέρνει όπου θέλει — έρχεται επιλεγμένο).
 */
export function freeOrigin(
  existing: Placed[],
  block: { count: number; columns: number; cell: Sized },
  canvas: CanvasSize,
  gapPx = GRID_GAP_PX
): Point | undefined {
  if (existing.length === 0) return undefined;
  const base = gridPositions({ ...block, canvas, gapPx });
  const first = base.positions[0];
  if (!first) return undefined;

  const bottom = Math.max(...existing.map(t => t.position_y + halfExtent(t, canvas).y));
  const half = halfExtent(block.cell, canvas);
  const marginY = canvas.height > 0 ? (gapPx / canvas.height) * 100 : 0;
  const y = snapUp(bottom + marginY + half.y);

  const rows = Math.ceil(block.count / Math.max(1, block.columns));
  const lastRowY = y + (rows - 1) * base.step.y;
  return lastRowY + half.y <= 100 ? { x: first.x, y } : undefined;
}

/**
 * Η πρώτη ελεύθερη θέση για ΕΝΑ νέο τραπέζι: σαρώνει το πλέγμα αριστερά→δεξιά,
 * πάνω→κάτω και κρατά το πρώτο κελί που δεν πέφτει πάνω σε υπάρχον τραπέζι.
 * Γεμάτος καμβάς: πάνω αριστερά (θα φανεί επιλεγμένο πάνω από τα άλλα).
 */
export function firstFreeSpot(existing: Placed[], cell: Sized, canvas: CanvasSize, gapPx = GRID_GAP_PX): Point {
  const columns = maxColumns(cell, canvas, gapPx);
  const { step, positions: [first] } = gridPositions({ count: 1, columns, cell, canvas, gapPx });
  const half = halfExtent(cell, canvas);
  const rows = Math.max(1, Math.floor((100 - half.y - first.y) / step.y) + 1);
  const { positions } = gridPositions({ count: columns * rows, columns, cell, canvas, gapPx });

  const free = positions.find(p => existing.every(t => {
    const other = halfExtent(t, canvas);
    return Math.abs(t.position_x - p.x) >= half.x + other.x || Math.abs(t.position_y - p.y) >= half.y + other.y;
  }));
  return free ?? first;
}

export type AlignMode = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom';

/** Στοίχιση της επιλογής στις άκρες (ή στο κέντρο) του περιγράμματός της. */
export function alignTables(tables: Placed[], mode: AlignMode, canvas: CanvasSize): Map<number, Point> {
  const out = new Map<number, Point>();
  if (tables.length < 2) return out;
  const boxes = tables.map(t => ({ t, half: halfExtent(t, canvas) }));

  const left = Math.min(...boxes.map(b => b.t.position_x - b.half.x));
  const right = Math.max(...boxes.map(b => b.t.position_x + b.half.x));
  const top = Math.min(...boxes.map(b => b.t.position_y - b.half.y));
  const bottom = Math.max(...boxes.map(b => b.t.position_y + b.half.y));

  for (const { t, half } of boxes) {
    let x = t.position_x, y = t.position_y;
    if (mode === 'left') x = left + half.x;
    if (mode === 'right') x = right - half.x;
    if (mode === 'centerX') x = (left + right) / 2;
    if (mode === 'top') y = top + half.y;
    if (mode === 'bottom') y = bottom - half.y;
    if (mode === 'centerY') y = (top + bottom) / 2;
    out.set(t.id, clampToCanvas({ x, y }, t, canvas));
  }
  return out;
}

/** Ίσες αποστάσεις ανάμεσα στα κέντρα, με τα δύο ακραία τραπέζια στη θέση τους. */
export function distributeTables(tables: Placed[], axis: 'x' | 'y', canvas: CanvasSize): Map<number, Point> {
  const out = new Map<number, Point>();
  if (tables.length < 3) return out;
  const key = axis === 'x' ? 'position_x' : 'position_y';
  const sorted = [...tables].sort((a, b) => a[key] - b[key]);
  const first = sorted[0][key];
  const step = (sorted[sorted.length - 1][key] - first) / (sorted.length - 1);

  sorted.forEach((t, i) => {
    const value = first + step * i;
    out.set(t.id, clampToCanvas(
      axis === 'x' ? { x: value, y: t.position_y } : { x: t.position_x, y: value }, t, canvas));
  });
  return out;
}

/**
 * Τακτοποίηση της επιλογής σε πλέγμα, με σειρά αριθμού τραπεζιού, ξεκινώντας
 * από την πάνω-αριστερή γωνία που καταλαμβάνει ήδη η επιλογή.
 */
export function arrangeInGrid(
  tables: Array<Placed & Pick<FloorTable, 'table_number'>>,
  columns: number,
  canvas: CanvasSize
): Map<number, Point> {
  const out = new Map<number, Point>();
  if (tables.length === 0) return out;
  const cell = {
    width: Math.max(...tables.map(t => t.width)),
    height: Math.max(...tables.map(t => t.height))
  };
  const half = halfExtent(cell, canvas);
  const left = Math.min(...tables.map(t => t.position_x - halfExtent(t, canvas).x));
  const top = Math.min(...tables.map(t => t.position_y - halfExtent(t, canvas).y));
  const origin = clampToCanvas({ x: snap(left + half.x), y: snap(top + half.y) }, cell, canvas);

  const sorted = [...tables].sort((a, b) => compareTableNumbers(a.table_number, b.table_number));
  const { positions } = gridPositions({ count: sorted.length, columns, cell, canvas, origin });
  sorted.forEach((t, i) => out.set(t.id, clampToCanvas(positions[i], t, canvas)));
  return out;
}

/** Τα τραπέζια που αγγίζει το ορθογώνιο επιλογής (συντεταγμένες σε ποσοστό). */
export function tablesInRect(tables: Placed[], a: Point, b: Point, canvas: CanvasSize): number[] {
  const left = Math.min(a.x, b.x), right = Math.max(a.x, b.x);
  const top = Math.min(a.y, b.y), bottom = Math.max(a.y, b.y);
  return tables
    .filter(t => {
      const half = halfExtent(t, canvas);
      return t.position_x + half.x >= left && t.position_x - half.x <= right
        && t.position_y + half.y >= top && t.position_y - half.y <= bottom;
    })
    .map(t => t.id);
}

/** Η κοινή τιμή ενός πεδίου στην επιλογή, ή undefined όταν διαφέρει. */
export function commonValue<T, K extends keyof T>(items: T[], key: K): T[K] | undefined {
  if (items.length === 0) return undefined;
  const first = items[0][key];
  return items.every(item => item[key] === first) ? first : undefined;
}
